// Phase 3b boundary contracts for map stores still authored in JS (roversHistory).
// These narrow types describe ONLY the fields map composables actually read, so the
// consumer stays free of `any` until the stores themselves migrate to TS.

import type {
	Baseline,
	BaseStationStyleFeatureData,
	FeatureData,
	FeatureType,
	GridpointFeatureData,
	RoverHistoryCurrentMetrics,
	RoverHistoryFragmentMetrics,
} from '@/components/map/types/mapFeatureTypes';
import type { GnssStatItem } from '@/components/map/types/gnssStatsTypes';
import type {
	BaseStationLiveStatusKey,
	BaseStationOnlineStatusKey,
	BaseStationStatusKey,
} from '@/utils/types/baseStationsTypes';

// Семантические алиасы ключей: структура dbItems это mountpoint -> gnss -> details.
// Это документирующие алиасы (структурно это просто string), для читаемости контракта.
// TODO(TS_MIGRATION): кандидат на перенос в самые базовые типы
export type Mountpoint = string;
export type GnssCode = string;

// dbItems shape shared by snr/multipath/residuals/gnss visibility stores.
// Also consumed directly by vrsAccuracyMetricsUtils.
// TODO(TS_MIGRATION): merge with the store-native types once those stores are in TS.
export type MetricDbItem = Record<GnssCode, GnssStatItem>;
export type MetricDbItems = Map<Mountpoint, MetricDbItem>;

// Overlay item: useMapContainer.getGnssFeatures -> mapFeaturesStore.updateFeatures.
export type MapFeatureOverlayItem = {
	featureType: FeatureType;
	featureData: FeatureData;
	coords: {
		left: string;
		top: string;
	};
};

// Tooltip payload shared by base-station and gridpoint map overlay entries.
export type GnssTooltipFeatureData = BaseStationStyleFeatureData | GridpointFeatureData;

// gnss tooltip overlay entry produced by *Store.getDisplayItems() ({...feature, stats}).
export type GnssTooltipDisplayItem = MapFeatureOverlayItem & {
	featureData: GnssTooltipFeatureData;
	stats: MetricDbItem | null;
};

// Passed to GnssTooltipsOverlay after useMapContainer adds route focus state.
export type GnssTooltipOverlayDisplayItem = GnssTooltipDisplayItem & {
	isFocused: boolean;
};

// Common subset of the per-mode stores kept in useMapData STORES.
export type MapModeStoreContract = {
	displayItems: GnssTooltipDisplayItem[];
	nonNullGnss: GnssCode[];
	dbItems: MetricDbItems;
};

// What useMapData reads from snrMultipathStore.factory(mode): narrowed to dbItems.
export type MetricFactoryStoreContract = Pick<MapModeStoreContract, 'dbItems'>;

export type PaasStddevValue = {
	value: number;
	days: number;
};

export type PaasStddevStoreContract = {
	items: Map<Mountpoint, PaasStddevValue>;
};

export type BaselineStationStored = {
	id: number;
	mntpnt_int: string;
	status: BaseStationStatusKey;
	status_live: BaseStationLiveStatusKey;
	deleted: boolean;
};

export type BaselineStoreItem = Omit<Baseline, 'status' | 'status_online'> & {
	status: BaseStationStatusKey;
	status_online: BaseStationOnlineStatusKey;
	base: BaselineStationStored;
	rover: BaselineStationStored;
	base_mntpnt_int: string;
	rover_mntpnt_int: string;
	description: string;
	nmea_port_ext: number | null;
	message: string;
	selected_gnss: string[];
};

export type BaselinesStoreContract = {
	items: BaselineStoreItem[];
};

export type RoverHistoryCurrentSelected = {
	b_coord: number;
	l_coord: number;
} & RoverHistoryCurrentMetrics;

export type RoverHistoryFragmentRaw = RoverHistoryFragmentMetrics & {
	b_coord: number;
	l_coord: number;
	b_coord2: number;
	l_coord2: number;
};

export type RoverPath = {
	sessionId: number;
	path: RoverHistoryFragmentRaw[];
};

export type RoversHistoryStoreContract = {
	showOnMap: boolean;
	autoUpdate: boolean;
	currentRoversSelected: RoverHistoryCurrentSelected[];
	roverPaths: RoverPath[];
};

// roversHistoryStore.js хранит в state значения из Api-хуков (any), из-за чего Pinia
// схлопывает выводимый тип state до пустого, и прямое присваивание стора контракту
// невозможно. Narrow в одной граничной точке, пока стор не переведён в TS.
// TODO(TS_MIGRATION): remove after roversHistoryStore migration to TS.
export const asRoversHistoryStore = (store: unknown): RoversHistoryStoreContract => (
	store as RoversHistoryStoreContract
);
